"use client";

import { useRef, useState, type PointerEvent } from "react";
import { Eraser, Pencil, RotateCcw, Sparkles, Trash2 } from "lucide-react";
import type { BrandingStudioCopy } from "./branding-page-copy";
import styles from "./branding-studio.module.scss";

type Point = { x: number; y: number };
type Stroke = { points: Point[]; colour: string };
const INKS = ["#17252a", "#8064c6", "#dc7045"];

export function CcBrandingDoodle({ copy }: { copy: BrandingStudioCopy["doodle"] }) {
  const [tool, setTool] = useState<"pen" | "eraser">("pen");
  const [colour, setColour] = useState(INKS[0]);
  const [history, setHistory] = useState<Stroke[][]>([[]]);
  const [draft, setDraft] = useState<Stroke | null>(null);
  const active = useRef<{ pointer: number; stroke: Stroke } | null>(null);
  const strokes = history[history.length - 1];

  function commit(next: Stroke[]) {
    setHistory((previous) => [...previous.slice(-39), next.slice(-150)]);
  }

  function point(event: PointerEvent<SVGSVGElement>): Point {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: (event.clientX - rect.left) / rect.width * 1000, y: (event.clientY - rect.top) / rect.height * 600 };
  }

  function start(event: PointerEvent<SVGSVGElement>) {
    if (!event.isPrimary || event.button !== 0 || active.current) return;
    const position = point(event);
    if (tool === "eraser") {
      const index = strokes.findLastIndex((stroke) => stroke.points.some((p) => Math.hypot(p.x - position.x, p.y - position.y) < 35));
      if (index >= 0) commit(strokes.filter((_, i) => i !== index));
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    const stroke = { points: [position], colour };
    active.current = { pointer: event.pointerId, stroke };
    setDraft(stroke);
  }

  function move(event: PointerEvent<SVGSVGElement>) {
    const current = active.current;
    if (!current || current.pointer !== event.pointerId) return;
    const next = point(event);
    const last = current.stroke.points[current.stroke.points.length - 1];
    if (Math.hypot(next.x - last.x, next.y - last.y) < 2 || current.stroke.points.length >= 800) return;
    current.stroke = { ...current.stroke, points: [...current.stroke.points, next] };
    setDraft(current.stroke);
  }

  function end(event: PointerEvent<SVGSVGElement>) {
    if (!active.current || active.current.pointer !== event.pointerId) return;
    commit([...strokes, active.current.stroke]);
    active.current = null;
    setDraft(null);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  }

  function addStar() {
    const cx = 330 + (strokes.length % 4) * 100;
    const cy = 250 + (strokes.length % 3) * 50;
    const points = Array.from({ length: 11 }, (_, i) => {
      const angle = i * Math.PI / 5 - Math.PI / 2;
      const radius = i % 2 === 0 ? 85 : 37;
      return { x: cx + Math.cos(angle) * radius, y: cy + Math.sin(angle) * radius };
    });
    commit([...strokes, { points, colour }]);
  }

  return (
    <div className={styles.doodle}>
      <div className={styles.doodleHeading}><span>{copy.label}</span><span className={styles.handwriting}>{copy.hint}<svg viewBox="0 0 70 45" aria-hidden="true"><path d="M4 7C43 0 56 13 47 34M35 25l12 11 14-10" /></svg></span></div>
      <svg className={styles.drawingSurface} viewBox="0 0 1000 600" preserveAspectRatio="none" role="img" aria-label={copy.canvasLabel} data-tool={tool} onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerCancel={end} onLostPointerCapture={end}>
        <g fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="5">
          <path className={styles.seedDoodle} d="M750 140l18 52 57 3-44 34 14 53-45-30-47 31 17-55-44-32 56-3zM735 309c22-10 44-10 64 0M222 442c42 16 93 17 149 3" />
          {[...strokes, ...(draft ? [draft] : [])].map((stroke, i) => <path key={i} stroke={stroke.colour} d={stroke.points.length === 1 ? `M${stroke.points[0].x} ${stroke.points[0].y}l0.1 0.1` : stroke.points.map((p, j) => `${j === 0 ? "M" : "L"}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ")} />)}
        </g>
      </svg>
      <div className={styles.doodleToolbar} role="group" aria-label={copy.canvasLabel}>
        <button type="button" title={copy.draw} aria-label={copy.draw} aria-pressed={tool === "pen"} onClick={() => setTool("pen")}><Pencil size={18} /></button>
        <button type="button" title={copy.erase} aria-label={copy.erase} aria-pressed={tool === "eraser"} onClick={() => setTool("eraser")}><Eraser size={18} /></button>
        <span className={styles.toolbarDivider} />
        {INKS.map((ink, i) => <button type="button" key={ink} className={styles.inkButton} style={{ "--ink": ink } as React.CSSProperties} aria-label={`${copy.colour}: ${copy.colours[i]}`} aria-pressed={colour === ink} onClick={() => { setColour(ink); setTool("pen"); }}><i /></button>)}
        <span className={styles.toolbarDivider} />
        <button type="button" title={copy.star} aria-label={copy.star} onClick={addStar}><Sparkles size={18} /></button>
        <button type="button" title={copy.undo} aria-label={copy.undo} disabled={history.length <= 1} onClick={() => setHistory((previous) => previous.slice(0, -1))}><RotateCcw size={18} /></button>
        <button type="button" title={copy.clear} aria-label={copy.clear} disabled={!strokes.length} onClick={() => commit([])}><Trash2 size={18} /></button>
      </div>
      <span className={styles.srOnly} aria-live="polite">{strokes.length} {copy.status}</span>
    </div>
  );
}
